import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { CONTACT, waLink } from "@/lib/config";
import { CheckCircle2, ArrowRight, Phone, MessageCircle, FileText, UserCheck, ListChecks, Sparkles } from "lucide-react";
import ContactForm from "@/components/ContactForm";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setSvc(null); setNotFound(false);
    api.get(`/services/${slug}`).then((r) => setSvc(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="h-display text-4xl text-slate-900">Service not found</h1>
          <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-blue-700">Browse all services <ArrowRight size={14} /></Link>
        </div>
      </PublicLayout>
    );
  }

  if (!svc) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">Loading…</div>
      </PublicLayout>
    );
  }

  const waMsg = waLink(`Hi KK Associates, I want a quote for "${svc.title}". Please share details and pricing.`);

  return (
    <PublicLayout>
      <section className="relative bg-slate-950 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 opacity-95" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 relative">
          <div className="text-xs uppercase tracking-widest text-blue-300">{svc.category}</div>
          <h1 className="h-display mt-3 text-5xl md:text-6xl max-w-4xl">{svc.title}</h1>
          <p className="mt-5 max-w-2xl text-slate-300 leading-relaxed">{svc.short_description}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={waMsg} target="_blank" rel="noreferrer" data-testid="svc-ask-price" className="btn-hover rounded-full bg-white text-slate-900 px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-blue-300">
              <MessageCircle size={14} /> Ask for Price
            </a>
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="svc-call" className="btn-hover rounded-full border border-white/25 px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-white/10">
              <Phone size={14} /> {CONTACT.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-12">
          {/* OVERVIEW */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700"><FileText size={14} /> Overview</div>
            <h2 className="h-display mt-2 text-3xl md:text-4xl text-slate-900">What's included</h2>
            <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">{svc.description}</p>
          </div>

          {/* WHO NEEDS */}
          {(svc.who_needs || []).length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700"><UserCheck size={14} /> Who is this for</div>
              <h3 className="h-display mt-2 text-2xl md:text-3xl text-slate-900">Best suited for</h3>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {svc.who_needs.map((w, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
                    <CheckCircle2 size={18} className="text-blue-700 mt-0.5" /><span className="text-sm">{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* BENEFITS */}
          {(svc.benefits || []).length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700"><Sparkles size={14} /> Key benefits</div>
              <h3 className="h-display mt-2 text-2xl md:text-3xl text-slate-900">Why clients choose us</h3>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {svc.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 rounded-xl border border-slate-200 px-4 py-3">
                    <CheckCircle2 size={18} className="text-emerald-600 mt-0.5" /><span className="text-sm">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* DOCUMENTS */}
          {(svc.documents_required || []).length > 0 && (
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700"><ListChecks size={14} /> Documents you'll need</div>
              <h3 className="h-display mt-2 text-2xl md:text-3xl text-slate-900">Ready checklist</h3>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {svc.documents_required.map((d, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700 rounded-xl bg-blue-50/40 border border-blue-100 px-4 py-3">
                    <FileText size={16} className="text-blue-700 mt-0.5" /><span className="text-sm">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* PROCESS */}
          {(svc.process_steps || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-700">Our process</div>
              <h3 className="h-display mt-2 text-2xl md:text-3xl text-slate-900">Four calm steps</h3>
              <ol className="mt-4 space-y-4">
                {svc.process_steps.map((s, i) => (
                  <li key={i} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-700 text-white flex items-center justify-center text-sm font-medium shrink-0">{i + 1}</div>
                    <div className="text-slate-700 pt-1">{s}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* PRICING NOTE */}
          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6">
            <div className="text-xs uppercase tracking-widest text-blue-700">Pricing</div>
            <h3 className="h-display text-2xl text-slate-900 mt-1">Every case is different — ask for a fair price</h3>
            <p className="mt-2 text-slate-700 text-sm leading-relaxed">
              We keep pricing transparent but personalised — because the same service can vary based on turnover,
              volumes, complexity and urgency. Share your details and we'll respond with a fixed-fee quote on WhatsApp.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href={waMsg} target="_blank" rel="noreferrer" className="btn-hover rounded-full bg-emerald-500 text-white px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-emerald-600">
                <MessageCircle size={14} /> Ask for Price on WhatsApp
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} className="btn-hover rounded-full border border-slate-300 text-slate-900 px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-100">
                <Phone size={14} /> Call {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="text-xs uppercase tracking-widest text-blue-700">Enquire about this service</div>
            <h4 className="h-display text-2xl text-slate-900 mt-2">Talk to a senior advisor</h4>
            <p className="mt-2 text-sm text-slate-600">Share your details — we'll respond on WhatsApp within a business day.</p>
            <div className="mt-5">
              <ContactForm defaultService={svc.title} compact testIdPrefix="svc" />
            </div>
          </div>
        </aside>
      </section>
    </PublicLayout>
  );
}
